import { Flex, Select, SimpleGrid, Title } from "@mantine/core";
import { DatePickerInput, DatesRangeValue } from "@mantine/dates";
import { useDisclosure } from "@mantine/hooks";
import { DataTableSortStatus } from "mantine-datatable";
import { useCallback, useState } from "react";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { useCompanyOptions } from "../../api/hooks/customer/useCompanyOptions.ts";
import SvgCalendar from "../../components/Icons/Calendar";
import Layout from "../../components/Layout/Layout";
import Preloader from "../../components/Preloader/Preloader.tsx";
import Table from "../../components/Table/Table";
import { SortDirection, TableRow } from "../../components/Table/types";
import { getCompanyOptions } from "../../utils/company.ts";
import { getDateTimeRangeFilter } from "../../utils/filter";
import { getPagesCount } from "../../utils/pagination";
import { DocumentType } from "../IDPForgeryCheck/types";
import { useIDPDatabaseData } from "./hooks/useIDPDatabaseData";
import { useIDPDatabaseTable } from "./hooks/useIDPDatabaseTable";
import StatusWarningModal from "./StatusWarningModal/StatusWarningModal";
import { DocumentDatabaseStatus } from "./types";

interface DatabaseTableRow extends TableRow {
  rawStatus: DocumentDatabaseStatus;
  documentType: string;
  companyName: string;
  fileName?: string;
}

const IDPDatabasePage = () => {
  const navigate = useNavigate();
  const [requestDateRange, setRequestDateRange] = useState<DatesRangeValue>([
    null,
    null,
  ]);
  const [documentType, setDocumentType] = useState<string | null>(null);
  const [customerId, setCustomerId] = useState<string | null>(null);

  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState<string>("createdAt");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  const [
    showStatusWarningModal,
    { open: openStatusWarningModal, close: closeStatusWarningModal },
  ] = useDisclosure(false);

  const [selectedDocument, setSelectedDocument] = useState<{
    id: number;
    status: DocumentDatabaseStatus;
    documentType: string;
    companyName: string;
    fileName?: string;
  } | null>(null);

  const [{ data: customersResponse, fetching: fetchingCustomers }] =
    useCompanyOptions();
  const customers = customersResponse?.customers.data;

  const requestDateFilter = getDateTimeRangeFilter(requestDateRange);

  useEffect(() => {
    setPage(1);
  }, [requestDateRange, documentType, customerId]);

  const {
    data: databaseResponse,
    loading,
    error,
  } = useIDPDatabaseData({
    page,
    limit: 20,
    sortField,
    sortDirection,
    filters: {
      dateRange: requestDateFilter,
      documentType,
      customerId,
    },
  });

  const databaseData = databaseResponse?.data || [];
  const pageCount = getPagesCount(databaseResponse?.total);

  const [columns, rows] = useIDPDatabaseTable({
    data: databaseData,
  });

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handleSortChange = useCallback((sort: DataTableSortStatus) => {
    setSortField(sort.columnAccessor);
    setSortDirection(sort.direction);
  }, []);

  const handleRowClick = useCallback(
    (record: DatabaseTableRow) => {
      const documentInfo = {
        id: Number(record.id),
        status: record.rawStatus,
        documentType: record.documentType,
        companyName: record.companyName,
        fileName: record.fileName,
      };

      setSelectedDocument(documentInfo);

      switch (record.rawStatus) {
        case DocumentDatabaseStatus.FAILED:
        case DocumentDatabaseStatus.UNREADABLE:
          openStatusWarningModal();
          break;

        case DocumentDatabaseStatus.PROCESSING:
          navigate(`/idp-forgery-check?id=${record.id}`);
          break;

        case DocumentDatabaseStatus.AVAILABLE:
          navigate(`/idp-forgery-check?id=${record.id}`);
          break;

        default:
          navigate(`/idp-forgery-check?id=${record.id}`);
      }
    },
    [navigate, openStatusWarningModal]
  );

  const handleStatusWarningClose = () => {
    closeStatusWarningModal();
    setSelectedDocument(null);
  };

  const handleStatusWarningRetry = () => {
    closeStatusWarningModal();
    navigate("/idp-forgery-check");
    setSelectedDocument(null);
  };

  const documentTypeOptions = Object.values(DocumentType).map((type) => ({
    value: type,
    label: type,
  }));

  if (error) {
    return (
      <Layout>
        <Flex
          direction="column"
          align="center"
          justify="center"
          style={{ height: "50vh" }}
        >
          <Title order={3} color="red">
            Error loading data
          </Title>
          <p>Please try again later.</p>
        </Flex>
      </Layout>
    );
  }

  return (
    <Layout>
      <Preloader loading={fetchingCustomers} />
      <Flex direction="column" gap={20}>
        <Title size="h1" style={{ flexShrink: 0 }}>
          Document Database
        </Title>

        <SimpleGrid
          cols={3}
          breakpoints={[
            { maxWidth: "sm", cols: 1 },
            { maxWidth: "md", cols: 2 },
            { maxWidth: "lg", cols: 3 },
          ]}
          spacing={16}
          verticalSpacing={8}
          style={{ flexShrink: 0 }}
        >
          <DatePickerInput
            label="Request Date Range"
            placeholder="Select Date Range"
            icon={<SvgCalendar />}
            firstDayOfWeek={0}
            type="range"
            size="m"
            clearable
            value={requestDateRange}
            onChange={setRequestDateRange}
          />

          <Select
            label="Document Type"
            placeholder="Select Document Type"
            value={documentType}
            onChange={setDocumentType}
            data={documentTypeOptions}
            clearable
            size="m"
          />

          <Select
            label="Company Name"
            placeholder="Select Customer Name"
            value={customerId}
            onChange={setCustomerId}
            data={getCompanyOptions(customers)}
            searchable
            clearable
            size="m"
            disabled={fetchingCustomers}
          />
        </SimpleGrid>

        <Table
          loading={loading}
          columns={columns}
          rows={rows}
          defaultSort={{
            columnAccessor: "createdAt",
            direction: SortDirection.desc,
          }}
          pagination={{
            pageCount: pageCount,
            page: page,
            onPageChange: handlePageChange,
          }}
          onSortChange={handleSortChange}
          onRowClick={handleRowClick as (record: TableRow) => void}
          highlightOnHover
        />
      </Flex>

      {showStatusWarningModal && (
        <StatusWarningModal
          opened={true}
          onClose={handleStatusWarningClose}
          onRetry={handleStatusWarningRetry}
          status={selectedDocument?.status || DocumentDatabaseStatus.FAILED}
          documentType={selectedDocument?.documentType}
          companyName={selectedDocument?.companyName}
        />
      )}
    </Layout>
  );
};

export default IDPDatabasePage;
